/**
 * Категории и подкатегории — те же ключи, что в Telegram-боте (регистрация).
 * Источник: shared/freelanceCategories.js (копия в ./data для CRA).
 */

// eslint-disable-next-line @typescript-eslint/no-var-requires
const catalog = require('./data/freelanceCategories');

export const MAIN_CATEGORY_LABELS: Record<string, string> = catalog.buildMainCategoryLabels();

/** Подкатегории по mainKey / orderKey */
export const SUBCATEGORY_LABELS: Record<string, Record<string, string>> = catalog.buildSubcategoryLabels();

/** Опции select заказов / фильтров (value = orderKey) */
export const ORDER_CATEGORY_OPTIONS: Array<{ value: string; label: string; profileKey: string }> =
  catalog.buildOrderCategoryOptions();

/** Опции алертов с subSource для подкатегорий */
export const ALERT_ORDER_CATEGORY_OPTIONS: Array<{
  value: string;
  label: string;
  subSource: string;
}> = catalog.buildAlertOrderCategoryOptions();

export function labelForMainCategoryKey(key: string): string {
  const k = String(key || '').trim();
  return MAIN_CATEGORY_LABELS[k] || catalog.getMainByKey(k)?.label || k;
}

/** Категория в заказах (поле orders.category): ключ → русская подпись; неизвестное значение — как ввели */
export function orderCategoryLabel(raw: unknown): string {
  const v = String(raw ?? '').trim();
  if (!v) return '—';
  if (v.startsWith('sub:') || v.startsWith('customMain:') || v.startsWith('customSub:')) {
    return formatCategoryToken(v);
  }
  return labelForMainCategoryKey(v);
}

export function labelForSubcategory(mainKey: string, subKey: string): string {
  const m = String(mainKey || '').trim();
  const s = String(subKey || '').trim();
  if (!s) return '';
  const byMain = SUBCATEGORY_LABELS[m];
  if (byMain && byMain[s]) return byMain[s];
  return catalog.getSubLabel(m, s) || s;
}

/** Подкатегории раздела для UI (по orderKey или profileKey) */
export function subcategoryEntriesForMain(mainOrOrderKey: string): Array<{ key: string; label: string }> {
  const main = catalog.getMainByKey(mainOrOrderKey);
  if (!main) return [];
  return (main.sub || []).map((s: { key: string; label: string }) => ({ key: s.key, label: s.label }));
}

/** Токен заказа: sub:<orderKey>:<sub> или plain orderKey */
export function buildOrderCategoryToken(mainOrOrderKey: string, subKey?: string): string {
  const main = catalog.getMainByKey(mainOrOrderKey);
  const orderKey = main ? catalog.orderKeyForMain(main) : String(mainOrOrderKey || '').trim();
  const sub = String(subKey || '').trim();
  if (sub && orderKey) return `sub:${orderKey}:${sub}`;
  return orderKey;
}

/** Одна запись из users.categories (sub:…, customMain:…, customSub:… или просто main key) */
export function formatCategoryToken(k: string): string {
  const raw = String(k || '').trim();
  if (!raw) return 'Не указано';

  if (raw.startsWith('sub:')) {
    const parts = raw.split(':');
    const mainKey = parts[1] || '';
    const subKey = parts.slice(2).join(':') || '';
    const mainLabel = labelForMainCategoryKey(mainKey);
    const subLabel = labelForSubcategory(mainKey, subKey);
    return subKey ? `${mainLabel} → ${subLabel}` : mainLabel;
  }

  if (raw.startsWith('customMain:')) {
    return raw.slice('customMain:'.length).trim() || 'Не указано';
  }

  if (raw.startsWith('customSub:')) {
    const parts = raw.split(':');
    const mainKey = parts[1] || '';
    const rest = parts.slice(2).join(':');
    const mainLabel = labelForMainCategoryKey(mainKey);
    return rest ? `${mainLabel} → ${rest}` : mainLabel;
  }

  return labelForMainCategoryKey(raw);
}

/**
 * Нормализация categories из БД (jsonb массив / строка / объект с ключами 0,1,… — как в node-pg).
 */
export function normalizeUserCategories(categories: unknown): string[] {
  try {
    if (categories == null) return [];

    if (typeof Buffer !== 'undefined' && Buffer.isBuffer?.(categories)) {
      return normalizeUserCategories(categories.toString('utf8'));
    }

    if (Array.isArray(categories)) {
      return categories.map((x) => String(x ?? '').trim()).filter(Boolean);
    }

    if (typeof categories === 'string') {
      const t = categories.trim();
      if (!t) return [];
      try {
        const p = JSON.parse(t);
        if (Array.isArray(p)) {
          return p.map((x) => String(x ?? '').trim()).filter(Boolean);
        }
        if (p && typeof p === 'object') {
          return normalizeUserCategories(p);
        }
      } catch {
        if (/^(sub:|customMain:|customSub:)/.test(t) || /^[a-z0-9_]+$/i.test(t)) {
          return [t];
        }
        return [];
      }
      return [];
    }

    if (typeof categories === 'object' && categories !== null) {
      const o = categories as Record<string, unknown>;
      const keys = Object.keys(o);
      if (keys.length && keys.every((k) => /^\d+$/.test(k))) {
        return keys
          .sort((a, b) => Number(a) - Number(b))
          .map((k) => String(o[k] ?? '').trim())
          .filter(Boolean);
      }
      return Object.values(o)
        .map((x) => (typeof x === 'string' ? x.trim() : String(x ?? '')))
        .filter(Boolean);
    }
    return [];
  } catch {
    return [];
  }
}

/**
 * Для профиля: из анкеты отделяем выбор из справочника (sub:, ключ основной категории)
 * от пользовательского ввода при регистрации (customMain:, customSub:).
 */
export function partitionProfileCategories(tokens: string[]): {
  standard: string[];
  custom: string[];
} {
  const standard: string[] = [];
  const custom: string[] = [];
  for (const k of tokens) {
    const s = String(k || '').trim();
    if (!s) continue;
    if (s.startsWith('customMain:') || s.startsWith('customSub:')) {
      custom.push(s);
    } else {
      standard.push(s);
    }
  }
  return { standard, custom };
}

/** Первая строка для карточек на главной */
export function shortCategoryLineFromUser(categories: unknown): string {
  const arr = normalizeUserCategories(categories);
  const first = arr.find((x) => x.length > 0);
  if (first == null) return 'Не указано';
  return formatCategoryToken(first);
}
